import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import MarketingShell from "@/components/MarketingShell";
import ScheduleModal from "@/components/marketing/ScheduleModal";
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, Mail,
  Send, Clock, Edit, X, Globe, Instagram, Linkedin, Facebook,
} from "lucide-react";
import { toast } from "sonner";
import useConfirm from "@/hooks/useConfirm";

const CHANNEL_COLORS = {
  email: { bg: "#e8f2fd", color: "var(--blue)", label: "Email", icon: Mail },
  instagram: { bg: "#fce4ec", color: "#E4405F", label: "Instagram", icon: Instagram },
  linkedin: { bg: "#e3eef9", color: "#0077B5", label: "LinkedIn", icon: Linkedin },
  facebook: { bg: "#e8eaf6", color: "#1877F2", label: "Facebook", icon: Facebook },
  twitter: { bg: "#f0f0f0", color: "#64748b", label: "X / Twitter", icon: Globe },
  sequence: { bg: "var(--teal-light)", color: "var(--teal)", label: "Sequence", icon: Send },
  draft: { bg: "var(--gray-light)", color: "var(--gray)", label: "Draft", icon: Clock },
};

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAYS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function MarketingCalendar() {
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState(searchParams.get("view") || "month");
  const [events, setEvents] = useState([]);

  // Load events for the currently-displayed month (± 1 month for week views).
  const loadEvents = useCallback(async () => {
    try {
      const y = currentDate.getFullYear();
      const m = currentDate.getMonth();
      const from = new Date(y, m - 1, 1).toISOString().slice(0, 10);
      const to = new Date(y, m + 2, 0).toISOString().slice(0, 10);
      const res = await axios.get(`${API}/marketing/calendar`, {
        params: { from, to },
        headers: { Authorization: `Bearer ${token}` },
      });
      setEvents(res.data?.items || []);
    } catch {
      setEvents([]);
    }
  }, [token, currentDate]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    const v = searchParams.get("view");
    if (v && v !== viewMode) setViewMode(v);
  }, [searchParams, viewMode]);
  const setActiveView = (m) => {
    setViewMode(m);
    const sp = new URLSearchParams(searchParams);
    if (m === "month") sp.delete("view"); else sp.set("view", m);
    setSearchParams(sp, { replace: true });
  };
  // Drag-drop reschedules update local `events` state immediately;
  // on the next `currentDate` change they get re-fetched from the
  // server which acts as the source of truth.
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [channelFilters, setChannelFilters] = useState(new Set(["email","instagram","linkedin","facebook","twitter","sequence","draft"]));
  const [dragItem, setDragItem] = useState(null);
  const confirm = useConfirm();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = new Date();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return events.filter(e => e.date === dateStr && channelFilters.has(e.channel));
  };

  const scheduledThisMonth = events.filter(e => e.date.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`)).length;

  const toggleFilter = (ch) => {
    const next = new Set(channelFilters);
    next.has(ch) ? next.delete(ch) : next.add(ch);
    setChannelFilters(next);
  };

  const handleDrop = useCallback(async (day) => {
    if (!dragItem) return;
    const newDate = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    const ev = events.find(e => e.id === dragItem);
    if (ev && ev.date !== newDate) {
      const ok = await confirm({
        title: "Reschedule event?",
        body: `Move "${ev.title}" to ${MONTHS[month]} ${day}?`,
        confirmLabel: "Reschedule",
        testid: "calendar-reschedule-confirm",
      });
      if (ok) {
        setEvents(prev => prev.map(e => e.id === dragItem ? { ...e, date: newDate } : e));
        toast.success(`"${ev.title}" rescheduled to ${MONTHS[month]} ${day}`);
      }
    }
    setDragItem(null);
  }, [dragItem, events, year, month, confirm]);

  // Upcoming 7 days
  const getUpcoming = () => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 7);
    return events.filter(e => {
      const d = new Date(e.date);
      return d >= start && d <= end;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const formatRelativeDay = (dateStr) => {
    const d = new Date(dateStr);
    const t = new Date();
    const diff = Math.floor((d - t) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    return DAYS[d.getDay()] + ", " + MONTHS[d.getMonth()].slice(0,3) + " " + d.getDate();
  };

  // Build calendar grid
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const [scheduleOpen, setScheduleOpen] = useState(false);

  return (
    <MarketingShell
      title="Campaign Calendar"
      subtitle={`${scheduledThisMonth} items scheduled this month`}
      actions={
        <>
          <div className="mkt-segmented">
            {["month", "week"].map(m => (
              <button key={m} onClick={() => setActiveView(m)}
                className={viewMode === m ? "active" : ""}
                style={{ textTransform: "capitalize" }}>{m}</button>
            ))}
          </div>
          <button onClick={prevMonth} className="btn" style={{ height: 32, padding: "0 9px" }}><ChevronLeft style={{ width: 14, height: 14 }} /></button>
          <button onClick={goToday} className="btn">Today</button>
          <button onClick={nextMonth} className="btn" style={{ height: 32, padding: "0 9px" }}><ChevronRight style={{ width: 14, height: 14 }} /></button>
          <button className="btn btn-primary" onClick={() => setScheduleOpen(true)} data-testid="schedule-item-btn">
            <Plus style={{ width: 13, height: 13 }} /> Schedule item
          </button>
        </>
      }
    >
    <div data-testid="marketing-calendar">
      {/* Two Column Layout */}
      <div style={{ display: "flex", gap: 16 }}>
        {/* Calendar Grid */}
        <div className="mkt-card" style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "0.5px solid var(--border)", fontFamily: "var(--font-head)", fontSize: 16, fontWeight: 620, color: "var(--navy)" }}>
            {MONTHS[month]} {year}
          </div>
          {/* Day Headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "0.5px solid var(--border)" }}>
            {DAYS.map(d => (
              <div key={d} style={{ padding: "8px 0", textAlign: "center", fontSize: 10, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{d}</div>
            ))}
          </div>
          {/* Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
            {cells.map((day, idx) => {
              const isToday = day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
              const dayEvents = day ? getEventsForDay(day) : [];
              return (
                <div key={idx}
                  onDragOver={day ? (e) => e.preventDefault() : undefined}
                  onDrop={day ? () => handleDrop(day) : undefined}
                  style={{
                    minHeight: 90, padding: "4px 6px", borderRight: "0.5px solid var(--border)", borderBottom: "0.5px solid var(--border)",
                    background: isToday ? "var(--blue-light)" : day ? "transparent" : "var(--page-bg)",
                    cursor: day ? "default" : "auto"
                  }}>
                  {day && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: isToday ? 700 : 400, color: isToday ? "var(--blue)" : "var(--text-secondary)", marginBottom: 3, display: "flex", alignItems: "center", justifyContent: "center", width: isToday ? 22 : "auto", height: isToday ? 22 : "auto", borderRadius: "50%", background: isToday ? "var(--blue)" : "none", color: isToday ? "#fff" : "var(--text-secondary)" }}>
                        {day}
                      </div>
                      {dayEvents.slice(0, 3).map(ev => {
                        const ch = CHANNEL_COLORS[ev.status === "draft" ? "draft" : ev.channel] || CHANNEL_COLORS.email;
                        const Icon = ch.icon;
                        return (
                          <div key={ev.id} draggable onDragStart={() => setDragItem(ev.id)}
                            onClick={() => setSelectedEvent(ev)}
                            style={{
                              padding: "2px 5px", borderRadius: 4, marginBottom: 2, cursor: "grab",
                              background: ch.bg, fontSize: 9, fontWeight: 500, color: ch.color,
                              display: "flex", alignItems: "center", gap: 3, overflow: "hidden", whiteSpace: "nowrap",
                              border: ev.status === "draft" ? "1px dashed var(--gray)" : "none"
                            }} title={ev.title}>
                            <Icon style={{ width: 9, height: 9, flexShrink: 0 }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</span>
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div style={{ fontSize: 9, color: "var(--text-tertiary)", padding: "1px 5px" }}>+{dayEvents.length - 3} more</div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Panel */}
        <div style={{ width: 260, flexShrink: 0, display: "flex", flexDirection: "column", gap: 12 }}>
          {/* Upcoming */}
          <div className="mkt-card mkt-card-pad">
            <h3 className="card-title" style={{ fontSize: 13, marginBottom: 10 }}>Upcoming this week</h3>
            {getUpcoming().length === 0 ? (
              <p style={{ fontSize: 11, color: "var(--text-tertiary)" }}>No items scheduled</p>
            ) : getUpcoming().map(ev => {
              const ch = CHANNEL_COLORS[ev.channel] || CHANNEL_COLORS.email;
              const Icon = ch.icon;
              return (
                <div key={ev.id} style={{ padding: "6px 0", borderBottom: "0.5px solid var(--border)", display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 5, background: ch.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                    <Icon style={{ width: 10, height: 10, color: ch.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 520, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ev.title}</div>
                    <div style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{formatRelativeDay(ev.date)} · {ev.time}</div>
                  </div>
                  <span style={{ fontSize: 8, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: ev.status === "scheduled" ? "var(--purple-light)" : "var(--gray-light)", color: ev.status === "scheduled" ? "var(--purple)" : "var(--gray)", flexShrink: 0 }}>{ev.status}</span>
                </div>
              );
            })}
          </div>

          {/* Filters */}
          <div className="mkt-card mkt-card-pad">
            <h3 className="card-title" style={{ fontSize: 13, marginBottom: 10 }}>Filter by channel</h3>
            {Object.entries(CHANNEL_COLORS).map(([key, ch]) => {
              const Icon = ch.icon;
              return (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", fontSize: 11, cursor: "pointer" }}>
                  <input type="checkbox" checked={channelFilters.has(key)} onChange={() => toggleFilter(key)} style={{ accentColor: ch.color }} />
                  <Icon style={{ width: 12, height: 12, color: ch.color }} />
                  <span>{ch.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>

      {/* Event Popover */}
      {selectedEvent && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setSelectedEvent(null)}>
          <div style={{ background: "var(--surface)", borderRadius: 14, width: 360, padding: "20px 24px", boxShadow: "var(--shadow-float)" }} onClick={e => e.stopPropagation()} data-testid="event-popover">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 className="card-title" style={{ fontSize: 15 }}>{selectedEvent.title}</h3>
              <button onClick={() => setSelectedEvent(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-tertiary)" }}><X style={{ width: 16, height: 16 }} /></button>
            </div>
            <div style={{ fontSize: 12, lineHeight: 2, color: "var(--text-secondary)" }}>
              <div><strong>Channel:</strong> {CHANNEL_COLORS[selectedEvent.channel]?.label || selectedEvent.channel}</div>
              <div><strong>Audience:</strong> {selectedEvent.audience}</div>
              <div><strong>Status:</strong> {selectedEvent.status}</div>
              <div><strong>Scheduled:</strong> {selectedEvent.date} at {selectedEvent.time}</div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn" style={{ flex: 1 }}><Edit style={{ width: 13, height: 13 }} /> Edit</button>
              <button className="btn" style={{ flex: 1 }} onClick={() => { toast.info("Open date picker to reschedule"); setSelectedEvent(null); }}><CalendarDays style={{ width: 13, height: 13 }} /> Reschedule</button>
            </div>
          </div>
        </div>
      )}
    </div>
    <ScheduleModal open={scheduleOpen} onClose={() => setScheduleOpen(false)} />
    </MarketingShell>
  );
}
